import { buildInboxIdeaEditPatch } from '../../utils/mindmap/ideaInbox';
import { IDEA_FONTS } from '../../config/nodeFonts';
import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { HocuspocusProvider } from '@hocuspocus/provider';
import { IndexeddbPersistence } from 'y-indexeddb';
import {
  applyCreateNodeTransaction,
  applyCreateInboxIdeaTransaction,
  applyMoveBranchToInboxTransaction,
  applyAttachInboxBranchTransaction,
  applyDeleteSubtreeTransaction,
  applyMoveNodesTransaction,
  applyConnectorEditsTransaction,
  applyResponsibilityTransaction,
  applyReparentTransaction,
  applyReplaceBranchWithMarkdownTransaction,
  applyReplaceBranchWithMapTransaction,
  applyTaskCompletionTransaction,
  beginUndoCaptureInteraction,
  endUndoCaptureInteraction,
  stripTransientNodeFields,
} from '../../utils/mindmap/yjsTransactions';
import { createYjsHistoryActions } from '../../utils/mindmap/yjsHistory';
import {
  createAwarenessState,
  filterAwarenessStates,
} from '../../utils/mindmap/awareness';
import {
  registerOfflineYjsDatabase,
} from '../../utils/mindmap/offlinePersistence';
import {
  MAX_CONFLICTED_NODES,
  buildConflictRecovery,
  acceptOfflineRecovery,
  buildRejectedAccessRecovery,
  clearOfflineNodeProposals,
  deleteOfflineNodeProposals,
  findLostNodeProposals,
  readOfflineNodeProposals,
  saveOfflineNodeProposal,
  saveOfflineRecovery,
  shouldPreserveRejectedCollaboration,
} from '../../utils/mindmap/offlineRecovery';
import {
  buildCollaborationCredential,
  getShareToken,
} from '../../utils/mindmap/apiClient';
import {
  createYjsProjection,
  createYjsProjectionFrameBatcher,
} from '../../utils/mindmap/yjsProjection';
import { createInitialYjsSyncMetrics } from '../../utils/mindmap/yjsSyncMetrics';

const getDefaultWsUrl = () => 'ws://localhost:4000/ws';
const relKey = (parentId, childId) => `${parentId}:${childId}`;
const LOCAL_RECOVERY_WAIT_MS = 3000;
const RECOVERY_GEOMETRY_ONLY_PATCH_KEYS = new Set(['position', 'layout', 'lastEditedBy']);

function boundedWait(promise, timeoutMs = LOCAL_RECOVERY_WAIT_MS) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), timeoutMs);
    Promise.resolve(promise).then(() => {
      clearTimeout(timer);
      resolve(true);
    }, () => {
      clearTimeout(timer);
      resolve(false);
    });
  });
}

function boundedValue(promise, fallback, timeoutMs = LOCAL_RECOVERY_WAIT_MS) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ completed: false, value: fallback }), timeoutMs);
    Promise.resolve(promise).then((value) => {
      clearTimeout(timer);
      resolve({ completed: true, value });
    }, () => {
      clearTimeout(timer);
      resolve({ completed: false, value: fallback });
    });
  });
}

function buildSnapshot(yNodes, yRels, yMeta) {
  const meta = yMeta.toJSON();
  return {
    nodes: Object.entries(yNodes.toJSON()).map(([id, data]) => ({ id, ...data })),
    relationships: Object.values(yRels.toJSON()),
    root: meta.root || null,
    meta,
  };
}

function deleteBrowserDatabase(databaseName) {
  if (
    !databaseName
    || typeof window === 'undefined'
    || typeof window.indexedDB?.deleteDatabase !== 'function'
  ) return Promise.resolve(false);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    };
    const timer = setTimeout(() => finish(false), LOCAL_RECOVERY_WAIT_MS);
    try {
      const request = window.indexedDB.deleteDatabase(databaseName);
      request.onsuccess = () => finish(true);
      request.onerror = () => finish(false);
      request.onblocked = () => finish(false);
    } catch {
      finish(false);
    }
  });
}

export function useYjsMap({
  mindmapId,
  initialData,
  authToken,
  readOnly = false,
  currentUserId = null,
  currentUserName = null,
  selectedNodeId = null,
  editingNodeId = null,
}) {
  const [state, setState] = useState(null);
  const [isSynced, setIsSynced] = useState(false);
  const [isLocallySynced, setIsLocallySynced] = useState(false);
  const [isNetworkSynced, setIsNetworkSynced] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [initialSyncMetrics, setInitialSyncMetrics] = useState(null);
  const [actions, setActions] = useState(null);
  const [awarenessStates, setAwarenessStates] = useState([]);
  const [recoveryNotice, setRecoveryNotice] = useState(null);
  const docRef = useRef(null);
  const providerRef = useRef(null);
  const undoManagerRef = useRef(null);
  const undoInteractionRef = useRef(null);
  const networkSyncedRef = useRef(false);
  const offlineNodeProposalsRef = useRef(new Map());
  const proposalWritesRef = useRef(Promise.resolve());
  const dirtyProposalIdsRef = useRef(new Set());
  const proposalFlushTimerRef = useRef(null);
  const accessRejectedRef = useRef(false);
  const connectionStatusRef = useRef('disconnected');
  const prepareRecoveryReloadRef = useRef(() => Promise.resolve());

  useEffect(() => {
    if (!mindmapId) {
      docRef.current = null;
      providerRef.current = null;
      undoManagerRef.current = null;
      undoInteractionRef.current = null;
      setState(null);
      setIsSynced(false);
      setIsLocallySynced(false);
      setIsNetworkSynced(false);
      setConnectionStatus('disconnected');
      setInitialSyncMetrics(null);
      setActions(null);
      setAwarenessStates([]);
      setRecoveryNotice(null);
      networkSyncedRef.current = false;
      offlineNodeProposalsRef.current.clear();
      proposalWritesRef.current = Promise.resolve();
      dirtyProposalIdsRef.current.clear();
      clearTimeout(proposalFlushTimerRef.current);
      proposalFlushTimerRef.current = null;
      accessRejectedRef.current = false;
      connectionStatusRef.current = 'disconnected';
      prepareRecoveryReloadRef.current = () => Promise.resolve();
      return;
    }

    // Reset sync flag when connecting to a (possibly new) document
    setIsSynced(false);
    setIsLocallySynced(false);
    setIsNetworkSynced(false);
    setConnectionStatus('connecting');
    connectionStatusRef.current = 'connecting';
    setInitialSyncMetrics(null);
    setRecoveryNotice(null);
    networkSyncedRef.current = false;
    offlineNodeProposalsRef.current.clear();
    proposalWritesRef.current = Promise.resolve();
    dirtyProposalIdsRef.current.clear();
    clearTimeout(proposalFlushTimerRef.current);
    proposalFlushTimerRef.current = null;
    accessRejectedRef.current = false;
    prepareRecoveryReloadRef.current = () => Promise.resolve();

    const doc = new Y.Doc();
    const yNodes = doc.getMap('nodes');
    const yRels = doc.getMap('relationships');
    const yMeta = doc.getMap('meta');
    const name = mindmapId; // IMPORTANT: keep this aligned with backend
    const wsUrl = process.env.REACT_APP_YJS_WS_URL || getDefaultWsUrl();
    const offlineDatabaseName = registerOfflineYjsDatabase(
      currentUserId,
      mindmapId
    );
    let persistence = null;
    if (offlineDatabaseName && typeof window !== 'undefined' && window.indexedDB) {
      try {
        persistence = new IndexeddbPersistence(offlineDatabaseName, doc);
      } catch {
        persistence = null;
      }
    }
    const localPersistenceReady = persistence
      ? new Promise((resolve) => persistence.once('synced', resolve))
      : Promise.resolve();

    let disposed = false;
    let rejectionInFlight = false;
    const preserveRejectedAccess = async () => {
      if (rejectionInFlight || accessRejectedRef.current || !currentUserId) return;
      rejectionInFlight = true;
      accessRejectedRef.current = true;
      networkSyncedRef.current = false;
      providerRef.current?.disconnect?.();
      connectionStatusRef.current = 'access-rejected';
      setConnectionStatus('access-rejected');
      setIsNetworkSynced(false);
      let localHydrationComplete = await boundedWait(localPersistenceReady);
      let recoverySave = Promise.resolve(false);
      let rejectedCacheCleared = false;
      let cacheClearInFlight = null;
      const clearRejectedSyncCache = async ({ force = false } = {}) => {
        if (rejectedCacheCleared) return true;
        if (!localHydrationComplete) return false;
        if (cacheClearInFlight) return cacheClearInFlight;
        cacheClearInFlight = (async () => {
          const saved = await recoverySave;
          if (!saved && !force) return false;
          const proposalClear = await boundedValue(
            clearOfflineNodeProposals(currentUserId, mindmapId),
            false
          );
          const proposalsCleared = proposalClear.completed && proposalClear.value;
          if (!proposalsCleared) return false;
          if (persistence?.clearData) {
            const cacheClear = await boundedValue(
              persistence.clearData().then(() => true).catch(() => false),
              false
            );
            const syncCacheCleared = cacheClear.completed && cacheClear.value;
            if (!syncCacheCleared) return false;
          } else if (!await deleteBrowserDatabase(offlineDatabaseName)) {
            return false;
          }
          offlineNodeProposalsRef.current.clear();
          dirtyProposalIdsRef.current.clear();
          rejectedCacheCleared = true;
          return true;
        })();
        const cleared = await cacheClearInFlight;
        if (!cleared) cacheClearInFlight = null;
        return cleared;
      };
      prepareRecoveryReloadRef.current = () => clearRejectedSyncCache({ force: true });

      const captureHydratedRecovery = async () => {
        flushOfflineProposals();
        await proposalWritesRef.current.catch(() => false);
        if (disposed) return;
        const recovery = buildRejectedAccessRecovery({
          userId: currentUserId,
          mindmapId,
          snapshot: buildSnapshot(yNodes, yRels, yMeta),
        });
        setRecoveryNotice({ ...recovery, persistenceStatus: 'saving' });
        recoverySave = boundedValue(
          saveOfflineRecovery(recovery).catch(() => false),
          false
        ).then(({ completed, value }) => completed && value);
        const saved = await recoverySave;
        if (disposed) return;
        setRecoveryNotice({
          ...recovery,
          persistenceStatus: saved ? 'saved' : 'failed',
        });
        // Once a durable recovery exists, remove the rejected Yjs update log
        // so a viewer reload starts from the server-approved document.
        if (saved) void clearRejectedSyncCache();
      };

      if (localHydrationComplete) {
        await captureHydratedRecovery();
        return;
      }

      setRecoveryNotice({
        ...buildRejectedAccessRecovery({
          userId: currentUserId,
          mindmapId,
          snapshot: buildSnapshot(yNodes, yRels, yMeta),
        }),
        hydrationPending: true,
        persistenceStatus: 'pending-hydration',
      });
      // y-indexeddb has no rejection path. Keep the source log intact and
      // refresh the recovery only if hydration positively completes.
      void localPersistenceReady.then(async () => {
        if (disposed) return;
        localHydrationComplete = true;
        await captureHydratedRecovery();
      });
    };

    const initialSyncCollector = createInitialYjsSyncMetrics();
    const provider = new HocuspocusProvider({
      url: wsUrl,
      name,
      document: doc,
      token: buildCollaborationCredential(authToken, getShareToken()),
      onMessage: ({ event }) => {
        initialSyncCollector.recordNetworkReceived(event?.data);
      },
      onStatus: ({ status }) => {
        connectionStatusRef.current = status;
        setConnectionStatus(status);
        if (status !== 'connected') {
          networkSyncedRef.current = false;
          setIsNetworkSynced(false);
        }
      },
      onAuthenticated: ({ scope }) => {
        if (shouldPreserveRejectedCollaboration({
          expectedReadOnly: readOnly,
          authenticatedScope: scope,
        })) void preserveRejectedAccess();
      },
      onAuthenticationFailed: ({ reason }) => {
        if (shouldPreserveRejectedCollaboration({
          expectedReadOnly: readOnly,
          authenticationFailureReason: reason,
        })) void preserveRejectedAccess();
      },
      onClose: ({ event } = {}) => {
        if (shouldPreserveRejectedCollaboration({
          expectedReadOnly: readOnly,
          closeEvent: event,
        })) void preserveRejectedAccess();
      },
      onAwarenessChange: ({ states }) => {
        setAwarenessStates(filterAwarenessStates(states));
      },
    });
    const initialAwarenessState = createAwarenessState({
      userId: currentUserId,
      displayName: currentUserName,
    });
    if (initialAwarenessState) {
      provider.awareness?.setLocalState(initialAwarenessState);
    } else {
      // Anonymous link visitors collaborate without claiming an account
      // identity or publishing a presence profile.
      provider.awareness?.setLocalState(null);
    }

    docRef.current = doc;
    providerRef.current = provider;

    // Create an UndoManager for collaborative undo/redo across the
    // nodes and relationships maps. This tracks changes at the Yjs
    // level so all collaborators see a consistent history.
    const undoManager = new Y.UndoManager([yNodes, yRels]);
    undoManagerRef.current = undoManager;
    const historyActions = createYjsHistoryActions(() => undoManagerRef.current);
    const canMutate = () => !readOnly && !accessRejectedRef.current;

    const projection = createYjsProjection({
      yNodes,
      yRelationships: yRels,
      yMeta,
    });
    const requestFrame = typeof window !== 'undefined' && window.requestAnimationFrame
      ? window.requestAnimationFrame.bind(window)
      : (callback) => setTimeout(callback, 0);
    const cancelFrame = typeof window !== 'undefined' && window.cancelAnimationFrame
      ? window.cancelAnimationFrame.bind(window)
      : clearTimeout;
    const projectionBatcher = createYjsProjectionFrameBatcher({
      requestFrame,
      cancelFrame,
      flush: (options) => {
        const snapshot = projection.flush(options);
        initialSyncCollector.recordProjection(snapshot?.projectionMetrics);
        setState(snapshot);
        return snapshot;
      },
    });
    const observeNodes = (event) => {
      projection.record('nodes', event);
      projectionBatcher.schedule();
    };
    const observeRelationships = (event) => {
      projection.record('relationships', event);
      projectionBatcher.schedule();
    };
    const observeMeta = (event) => {
      projection.record('meta', event);
      projectionBatcher.schedule();
    };
    const recordUpdateBytes = (update, origin) => {
      projectionBatcher.addUpdateBytes(update?.byteLength || 0);
      if (origin === provider) {
        initialSyncCollector.recordNetworkAppliedUpdate(update);
      } else if (origin === persistence) {
        initialSyncCollector.recordLocalAppliedUpdate(update);
      }
    };

    // observe changes
    yNodes.observe(observeNodes);
    yRels.observe(observeRelationships);
    yMeta.observe(observeMeta);
    doc.on('update', recordUpdateBytes);

    // initial render
    const initialProjection = projection.flush({ full: true });
    initialSyncCollector.recordProjection(initialProjection?.projectionMetrics);
    setState(initialProjection);

    const acceptNetworkState = ({ allowInitialSeed = true } = {}) => {
      if (allowInitialSeed && initialData && !readOnly) {
        doc.transact(() => {
          if (yNodes.size !== 0 || yRels.size !== 0) return;

          // seed nodes
          if (Array.isArray(initialData.nodes)) {
            for (const node of initialData.nodes) {
              if (!node?.id) continue;
              const { id, ...rest } = stripTransientNodeFields(node);
              yNodes.set(id, rest);
            }
          }

          // seed relationships
          if (Array.isArray(initialData.relationships)) {
            for (const rel of initialData.relationships) {
              if (!rel?.parentId || !rel?.childId) continue;
              yRels.set(relKey(rel.parentId, rel.childId), {
                parentId: rel.parentId,
                childId: rel.childId,
              });
            }
          }

          // seed root only (NOT selectedId)
          if (initialData.root) yMeta.set('root', initialData.root);

          // seed any extra meta keys (avoid root/selectedId)
          if (initialData.meta && typeof initialData.meta === 'object') {
            for (const [k, v] of Object.entries(initialData.meta)) {
              if (k === 'root' || k === 'selectedId') continue;
              yMeta.set(k, v);
            }
          }
        });
      }

      projectionBatcher.schedule({
        full: true,
        afterFlush: (snapshot) => {
          setIsSynced(true);
          setInitialSyncMetrics(initialSyncCollector.complete({
            nodeCount: snapshot?.nodes?.length,
            relationshipCount: snapshot?.relationships?.length,
          }));
        },
      });
    };

    const persistedProposalsPromise = currentUserId
      ? readOfflineNodeProposals(currentUserId, mindmapId).catch(() => new Map())
      : Promise.resolve(new Map());
    let networkSyncInFlight = false;
    let localHydrationRetryScheduled = false;
    let proposalReadRetryScheduled = false;
    let persistedProposalsConsumed = false;

    const flushOfflineProposals = () => {
      clearTimeout(proposalFlushTimerRef.current);
      proposalFlushTimerRef.current = null;
      if (!currentUserId || !dirtyProposalIdsRef.current.size) return;
      const dirtyNodeIds = [...dirtyProposalIdsRef.current];
      dirtyProposalIdsRef.current.clear();
      const proposals = dirtyNodeIds
        .map((nodeId) => offlineNodeProposalsRef.current.get(nodeId))
        .filter(Boolean);
      proposalWritesRef.current = proposalWritesRef.current
        .catch(() => false)
        .then(async () => {
          for (const node of proposals) {
            await saveOfflineNodeProposal({ userId: currentUserId, mindmapId, node });
          }
        })
        .catch(() => false);
    };

    const scheduleProposalFlush = () => {
      clearTimeout(proposalFlushTimerRef.current);
      proposalFlushTimerRef.current = setTimeout(flushOfflineProposals, 200);
    };

    const rememberOfflineProposal = (nodeId) => {
      if (networkSyncedRef.current || accessRejectedRef.current || !nodeId) return;
      const value = yNodes.get(nodeId);
      if (!value) return;
      offlineNodeProposalsRef.current.delete(nodeId);
      if (offlineNodeProposalsRef.current.size >= MAX_CONFLICTED_NODES) {
        const oldestNodeId = offlineNodeProposalsRef.current.keys().next().value;
        offlineNodeProposalsRef.current.delete(oldestNodeId);
      }
      offlineNodeProposalsRef.current.set(nodeId, { id: nodeId, ...value });
      if (currentUserId) {
        dirtyProposalIdsRef.current.add(nodeId);
        scheduleProposalFlush();
      }
    };

    const rememberPatchedNode = (nodeId, patch) => {
      if (Object.keys(patch || {}).some((key) => !RECOVERY_GEOMETRY_ONLY_PATCH_KEYS.has(key))) {
        rememberOfflineProposal(nodeId);
      }
    };

    const forgetOfflineProposals = (nodeIds) => {
      const ids = [...new Set((nodeIds || []).filter(Boolean))];
      ids.forEach((nodeId) => {
        offlineNodeProposalsRef.current.delete(nodeId);
        dirtyProposalIdsRef.current.delete(nodeId);
      });
      if (!currentUserId || !ids.length) return;
      proposalWritesRef.current = proposalWritesRef.current
        .catch(() => false)
        .then(() => deleteOfflineNodeProposals(currentUserId, mindmapId, ids))
        .catch(() => false);
    };

    const onNetworkSynced = async ({ state: syncedState } = {}) => {
      if (syncedState === false || networkSyncInFlight || accessRejectedRef.current) return;
      networkSyncInFlight = true;
      const [proposalRead, localHydrationComplete] = await Promise.all([
        boundedValue(persistedProposalsPromise, new Map()),
        boundedWait(localPersistenceReady),
      ]);
      if (proposalRead.completed && !persistedProposalsConsumed) {
        persistedProposalsConsumed = true;
        for (const [nodeId, proposal] of proposalRead.value) {
          if (!offlineNodeProposalsRef.current.has(nodeId)) {
            offlineNodeProposalsRef.current.set(nodeId, proposal);
          }
        }
      }
      if (
        disposed
        || accessRejectedRef.current
        || connectionStatusRef.current !== 'connected'
      ) {
        networkSyncInFlight = false;
        return;
      }
      if (!localHydrationComplete || !proposalRead.completed) {
        // Keep the connected server projection usable, but do not claim a
        // reconciled sync or clear any recovery proposal until the local log
        // has positively hydrated.
        acceptNetworkState({ allowInitialSeed: false });
        if (!localHydrationComplete && !localHydrationRetryScheduled) {
          localHydrationRetryScheduled = true;
          void localPersistenceReady.then(() => {
            localHydrationRetryScheduled = false;
            if (!disposed && !accessRejectedRef.current) {
              void onNetworkSynced({ state: true });
            }
          });
        }
        if (!proposalRead.completed && !proposalReadRetryScheduled) {
          proposalReadRetryScheduled = true;
          void persistedProposalsPromise.then(() => {
            proposalReadRetryScheduled = false;
            if (!disposed && !accessRejectedRef.current) {
              void onNetworkSynced({ state: true });
            }
          });
        }
        networkSyncInFlight = false;
        return;
      }
      networkSyncedRef.current = true;
      flushOfflineProposals();
      await proposalWritesRef.current.catch(() => false);
      if (
        disposed
        || accessRejectedRef.current
        || connectionStatusRef.current !== 'connected'
      ) {
        networkSyncedRef.current = false;
        networkSyncInFlight = false;
        return;
      }
      acceptNetworkState();
      const proposals = offlineNodeProposalsRef.current;
      const currentProposalNodes = new Map([...proposals.keys()].map((nodeId) => {
        const value = yNodes.get(nodeId);
        return [nodeId, value ? { id: nodeId, ...value } : null];
      }));
      const conflicts = proposals.size
        ? findLostNodeProposals(proposals, currentProposalNodes)
        : [];
      let mayClearProposals = conflicts.length === 0;
      if (conflicts.length && currentUserId) {
        const snapshot = buildSnapshot(yNodes, yRels, yMeta);
        const recovery = buildConflictRecovery({
          userId: currentUserId,
          mindmapId,
          conflicts,
          snapshot,
        });
        setRecoveryNotice({ ...recovery, persistenceStatus: 'saving' });
        const recoverySave = await boundedValue(
          saveOfflineRecovery(recovery).catch(() => false),
          false
        );
        mayClearProposals = recoverySave.completed && recoverySave.value;
        if (disposed) return;
        setRecoveryNotice({
          ...recovery,
          persistenceStatus: mayClearProposals ? 'saved' : 'failed',
        });
      }
      if (currentUserId && proposals.size && mayClearProposals) {
        const proposalClear = await boundedValue(
          clearOfflineNodeProposals(currentUserId, mindmapId),
          false
        );
        if (proposalClear.completed && proposalClear.value) {
          offlineNodeProposalsRef.current.clear();
        }
      }
      if (!disposed && !accessRejectedRef.current) setIsNetworkSynced(true);
      networkSyncInFlight = false;
    };
    const onLocalSynced = () => {
      if (disposed) return;
      setIsLocallySynced(true);
      if (yNodes.size > 0 || yRels.size > 0 || yMeta.size > 0) {
        projectionBatcher.schedule({
          full: true,
          afterFlush: () => setIsSynced(true),
        });
      }
    };

    provider.on('synced', onNetworkSynced);
    void localPersistenceReady.then(onLocalSynced);
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', flushOfflineProposals);
    }

    // Expose a stable set of actions bound to this document.
    setActions({
      setRoot(rootId) {
        if (!canMutate()) return false;
        doc.transact(() => yMeta.set('root', rootId));
        return true;
      },

      setTheme(themeId) {
        if (!canMutate()) return false;
        doc.transact(() => {
          if (themeId) {
            yMeta.set('theme', themeId);
          } else {
            yMeta.delete('theme');
          }
        });
        return true;
      },

      setDefaultFontFamily(fontId) {
        if (!canMutate() || (fontId && !IDEA_FONTS.some(font => font.id === fontId))) return false;
        doc.transact(() => {
          if (fontId && fontId !== 'theme') yMeta.set('defaultFontFamily', fontId);
          else yMeta.delete('defaultFontFamily');
        });
        return true;
      },

      setConnectionStyle(connectionStyleId) {
        if (!canMutate()) return false;
        doc.transact(() => {
          if (connectionStyleId) {
            yMeta.set('connectionStyle', connectionStyleId);
          } else {
            yMeta.delete('connectionStyle');
          }
        });
        return true;
      },

      addNode(node) {
        if (!canMutate()) return false;
        if (!node?.id) return;
        const { id, ...rest } = stripTransientNodeFields(node);
        doc.transact(() => yNodes.set(id, rest));
        rememberOfflineProposal(id);
        return true;
      },

      editInboxIdea(id, edit) {
        if (!canMutate()) return false;
        const patch = buildInboxIdeaEditPatch({ ...edit, actorUserId: currentUserId, node: yNodes.get(id) });
        if (!Object.keys(patch).length) return true;
        undoManagerRef.current?.stopCapturing();
        doc.transact(() => yNodes.set(id, stripTransientNodeFields({ ...yNodes.get(id), ...patch, lastEditedBy: currentUserId })));
        undoManagerRef.current?.stopCapturing();
        rememberPatchedNode(id, patch);
        return true;
      },

      updateNode(id, patch) {
        if (!canMutate()) return false;
        if (!id) return;
        doc.transact(() => {
          const cur = yNodes.get(id) || {};
          yNodes.set(id, stripTransientNodeFields({ ...cur, ...patch }));
        });
        rememberPatchedNode(id, patch);
        return true;
      },

      assignTaskResponsibility(nodeId, assignee, actorUserId) {
        if (!canMutate()) return false;
        const result = applyResponsibilityTransaction({
          doc,
          yNodes,
          undoManager: undoManagerRef.current,
          nodeId,
          assignee,
          actorUserId,
        });
        rememberOfflineProposal(nodeId);
        return result;
      },

      completeTask(nodeId, done, actorUserId) {
        if (!canMutate()) return false;
        const result = applyTaskCompletionTransaction({
          doc,
          yNodes,
          undoManager: undoManagerRef.current,
          nodeId,
          done,
          actorUserId,
        });
        rememberOfflineProposal(nodeId);
        return result;
      },

      deleteNode(id) {
        if (!canMutate()) return false;
        if (!id) return;
        const toDelete = new Set();
        const collect = (nodeId) => {
          if (!nodeId || toDelete.has(nodeId)) return;
          toDelete.add(nodeId);
          for (const [, rel] of yRels.entries()) {
            if (rel?.parentId === nodeId) collect(rel.childId);
          }
        };
        collect(id);
        doc.transact(() => {
          for (const nodeId of toDelete) {
            yNodes.delete(nodeId);
          }

          for (const [key, rel] of yRels.entries()) {
            if (rel && (toDelete.has(rel.parentId) || toDelete.has(rel.childId))) {
              yRels.delete(key);
            }
          }
        });
        forgetOfflineProposals([...toDelete]);
        return true;
      },

      deleteSubtree(command) {
        if (!canMutate()) return false;
        const result = applyDeleteSubtreeTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
        });
        forgetOfflineProposals(command?.nodeIds);
        return result;
      },

      replaceBranchWithMap(command) {
        if (!canMutate()) return false;
        const result = applyReplaceBranchWithMapTransaction({ doc, yNodes, yRels, undoManager: undoManagerRef.current, command });
        forgetOfflineProposals(command.branchNodeIds.filter(id => id !== command.nodeId));
        rememberOfflineProposal(command.nodeId);
        return result;
      },

      replaceBranchWithMarkdown(command) {
        if (!canMutate()) return false;
        const result = applyReplaceBranchWithMarkdownTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
        });
        forgetOfflineProposals(command?.removedNodeIds);
        rememberOfflineProposal(command?.nodeId);
        return result;
      },

      addRelationship(parentId, childId) {
        if (!canMutate()) return false;
        if (!parentId || !childId) return;
        const key = relKey(parentId, childId);
        doc.transact(() => yRels.set(key, { parentId, childId }));
        return true;
      },

      createChild(command) {
        if (!canMutate()) return false;
        const result = applyCreateNodeTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
        });
        rememberOfflineProposal(command?.node?.id);
        return result;
      },

      createNodeWithRelationship(command) {
        if (!canMutate()) return false;
        const result = applyCreateNodeTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
        });
        rememberOfflineProposal(command?.node?.id);
        return result;
      },

      createInboxIdea(command) {
        if (!canMutate()) return false;
        const result = applyCreateInboxIdeaTransaction({
          doc,
          yNodes,
          undoManager: undoManagerRef.current,
          command,
        });
        rememberOfflineProposal(command?.node?.id);
        return result;
      },

      moveBranchToInbox(command) {
        if (!canMutate()) return false;
        const result = applyMoveBranchToInboxTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
        });
        rememberOfflineProposal(command?.nodeId);
        return result;
      },

      attachInboxBranch(command, options = {}) {
        if (!canMutate()) return false;
        const result = applyAttachInboxBranchTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
          lastEditedBy: options.lastEditedBy || null,
        });
        rememberOfflineProposal(command?.nodeId);
        return result;
      },

      beginInteraction() {
        if (!canMutate()) return false;
        return beginUndoCaptureInteraction(
          undoManagerRef.current,
          undoInteractionRef
        );
      },

      editConnectors(command, options = {}) {
        if (!canMutate()) return false;
        return applyConnectorEditsTransaction({ doc, yNodes, undoManager: undoManagerRef.current, command, lastEditedBy: options.lastEditedBy || null });
      },

      moveNodes(command, options = {}) {
        if (!canMutate()) return false;
        return applyMoveNodesTransaction({
          doc,
          yNodes,
          undoManager: undoManagerRef.current,
          command,
          lastEditedBy: options.lastEditedBy || null,
          isolate: !!options.isolate,
        });
      },

      reparentSubtree(command, options = {}) {
        if (!canMutate()) return false;
        return applyReparentTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
          lastEditedBy: options.lastEditedBy || null,
          isolate: !undoInteractionRef.current,
        });
      },

      endInteraction() {
        if (!canMutate()) return false;
        return endUndoCaptureInteraction(undoInteractionRef);
      },

      deleteRelationship(parentId, childId) {
        if (!canMutate()) return false;
        const key = relKey(parentId, childId);
        doc.transact(() => yRels.delete(key));
        return true;
      },

      exportSnapshot() {
        return projection.readFullSnapshot();
      },

      ...(readOnly ? {
        undo: () => false,
        redo: () => false,
        canUndo: () => false,
        canRedo: () => false,
      } : {
        undo: () => (canMutate() ? historyActions.undo() : false),
        redo: () => (canMutate() ? historyActions.redo() : false),
        canUndo: () => (canMutate() ? historyActions.canUndo() : false),
        canRedo: () => (canMutate() ? historyActions.canRedo() : false),
      }),
    });

    return () => {
      disposed = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', flushOfflineProposals);
      }
      flushOfflineProposals();
      endUndoCaptureInteraction(undoInteractionRef);
      provider.off('synced', onNetworkSynced);

      projectionBatcher.dispose();
      yNodes.unobserve(observeNodes);
      yRels.unobserve(observeRelationships);
      yMeta.unobserve(observeMeta);
      doc.off('update', recordUpdateBytes);

      provider.destroy();
      doc.destroy();

      undoManagerRef.current = null;
      providerRef.current = null;
      docRef.current = null;
      setActions(null);
      setAwarenessStates([]);
    };
  }, [
    mindmapId,
    initialData,
    authToken,
    readOnly,
    currentUserId,
    currentUserName,
  ]);

  useEffect(() => {
    const presence = createAwarenessState({
      userId: currentUserId,
      displayName: currentUserName,
      selectedNodeId,
      editingNodeId,
    });
    if (!providerRef.current?.awareness) return undefined;
    if (!presence) {
      providerRef.current.awareness.setLocalState(null);
      return undefined;
    }

    const timer = setTimeout(() => {
      providerRef.current?.awareness?.setLocalState(presence);
    }, 100);
    return () => clearTimeout(timer);
  }, [
    currentUserId,
    currentUserName,
    selectedNodeId,
    editingNodeId,
    mindmapId,
  ]);

  return {
    yState: state,
    actions,
    yDoc: docRef.current,
    yProvider: providerRef.current,
    awarenessStates,
    connectionStatus,
    isLocallySynced,
    isNetworkSynced,
    isSynced,
    initialSyncMetrics,
    recoveryNotice,
    clearRecoveryNotice: async () => {
      const notice = recoveryNotice;
      if (!notice || notice.kind !== 'whole-node-conflict') return false;
      // Serialize acceptance after pending writes so they cannot resurrect a
      // reviewed proposal on the next page load.
      const acceptance = proposalWritesRef.current.catch(() => false)
        .then(() => acceptOfflineRecovery(notice)).catch(() => false);
      proposalWritesRef.current = acceptance;
      if (!await acceptance) return false;
      for (const conflict of notice.conflicts) {
        if (offlineNodeProposalsRef.current.get(conflict.nodeId) === conflict.localProposal) {
          offlineNodeProposalsRef.current.delete(conflict.nodeId);
          dirtyProposalIdsRef.current.delete(conflict.nodeId);
        }
      }
      setRecoveryNotice((current) => current === notice ? null : current);
      return true;
    },
    prepareRecoveryReload: () => prepareRecoveryReloadRef.current(),
  };
}
